import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { BASE_URL } from '@/config/api';
import { Mail, Send, UserPlus, Loader2, Link2, Trophy, Users, Zap, CheckCircle, Star, Award, Target, Calendar, Settings, School, AlertTriangle, LogOut, Building, UserX, GraduationCap } from 'lucide-react';

interface InvitationJoinStudentManagerProps {
  userType: 'school' | 'coach';
  onTabChange?: (tab: string) => void;
  onShowSuccessModal?: (show: boolean) => void;
}

const InvitationJoinStudentManager: React.FC<InvitationJoinStudentManagerProps> = ({ userType, onTabChange, onShowSuccessModal }) => {
  const { toast } = useToast();
  const { token, user, refreshUserData } = useAuth();
  const { t, locale } = useLanguage();
  const queryClient = useQueryClient();
  
  const [email, setEmail] = useState('');
  const [invitationCode, setInvitationCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showLeaveConfirmModal, setShowLeaveConfirmModal] = useState(false);
  const [schoolJoined, setSchoolJoined] = useState(null);

  useEffect(() => {
    const showModal = localStorage.getItem('showSuccessModal');
      if (showModal === 'true') {
        setShowSuccessModal(true);
        localStorage.removeItem('showSuccessModal');
      }
    }, []);

  const formatDate = (dateString: string) => {
    if (!dateString) {
      return 'Date Invalide';
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Date Invalide'; 
    }

    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'en-US').format(date);
  };

  // Fetch invitations for school accounts
  const { data: invitations = [], isLoading, refetch } = useQuery({
    queryKey: ['school-invitations', token],
    queryFn: async () => {
      if (userType !== 'school') return [];
      
      const response = await fetch(`${BASE_URL}/api/school/invitations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch invitations');
      }

      const data = await response.json();
      return data || [];
    },
    enabled: userType === 'school',
  });

  // Send invitation (for schools)
  const handleSendInvitation = async () => {
    if (!email) {
      toast({
        title: "Error",
        description: "Please enter an email address",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${BASE_URL}/api/school/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send invitation');
      }

      toast({
        title: "Invitation sent",
        description: `Invitation sent to ${email} successfully`,
      });

      setEmail('');
      refetch();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Join school (for coaches)
  const handleJoinSchool = async () => {
    if (!invitationCode) {
      toast({
        title: t('common.error'),
        description: t('invitations.pleaseEnterCode'),
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${BASE_URL}/api/coach/join/school`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: invitationCode }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to join school');
      }

      const result = await response.json();

      if(result.status === "exists") {
        toast({
          title: t('common.error'),
          description: t('invitations.alreadyRegistered'),
          variant: 'destructive',
        });
        return false;
      }
      
      setSchoolJoined(result.school || { name: t('accounts.profile.teacherConnectedToSchool') });
      setInvitationCode('');
      //.setItem('showSuccessModal', 'true');
      await refreshUserData();
      //setShowSuccessModal(true);

      if (onShowSuccessModal) {
        onShowSuccessModal(true); // Appeler la fonction pour afficher la modal
        if (onTabChange) {
          onTabChange('profile');
        }
      }


      queryClient.invalidateQueries({ queryKey: ['user'] });

    } catch (error) {
      console.log(error);
       toast({
          title: t('common.error'),
          description: t('invitations.invalidCode'),
          variant: "destructive",
        });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Leave school (for teachers_all accounts)
  const handleLeaveSchool = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${BASE_URL}/api/coach/leave/school`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to leave school');
      }

      toast({
        title: "École quittée",
        description: "Vous avez quitté l'école avec succès",
      });

      await refreshUserData();
      queryClient.invalidateQueries({ queryKey: ['user'] });
      setShowLeaveConfirmModal(false);

    } catch (error) {
      toast({
        title: "Erreur",
        description: error.message || "Erreur lors de la déconnexion de l'école",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    setShowSuccessModal(false);
    if (onTabChange) {
      onTabChange('profile');
    }
  };

  if (userType === 'school') {
    return (
      <div className="space-y-6 p-5">
        {/* Send Invitation Section */}
        <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 via-white to-indigo-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl text-blue-900">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Send className="h-5 w-5 text-blue-600" />
              </div>
             {t('invitations.inviteStudent')}
            </CardTitle>
            <CardDescription className="text-blue-700">
              {t('invitations.inviteStudentDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="coach-email" className="text-blue-900 font-medium">{t('invitations.studentEmail')}</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-400" />
                  <Input
                    id="coach-email"
                    type="email"
                    placeholder="student@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 border-blue-200 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <Button 
                  onClick={handleSendInvitation}
                  disabled={isSubmitting}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6"
                >
                  {isSubmitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      {t('invitations.sendInvitation')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invitations List */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className='text-lg text-gray-800'>{t('invitations.sentInvitations')}</CardTitle>
            <CardDescription>
              {t('invitations.sentInvitationStudentDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : invitations.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                {t('invitations.noInvitationsSentYet')}
              </p>
            ) : (
              <div className="space-y-3">
                {invitations.map((invitation, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{invitation.email}</p>
                    </div>
                    <Badge 
                      variant="secondary"
                      className="bg-green-100 text-green-800"
                    >
                      {t('invitations.sent')} {formatDate(invitation.date_created)}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if user is already connected to a school
  if (user?.account_type === 'C' && user?.roleType === "teachers_all") {
    return (
      <>
        <div className="min-h-[600px] bg-gradient-to-br from-amber-50 via-white to-orange-50 rounded-xl p-8 relative overflow-hidden">
          {/* Background decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-amber-200/30 to-orange-200/30 rounded-full blur-3xl -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-blue-200/30 to-purple-200/30 rounded-full blur-3xl -ml-24 -mb-24"></div>
          
          <div className="relative z-10">
            {/* Header Section */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full mb-6 shadow-lg">
                <School className="h-10 w-10 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {t('invitations.connectedSchool')}
              </h1>
              <p className="text-lg text-gray-600 max-w-md mx-auto">
                {t('invitations.connectedSchoolDesc')}
              </p>
            </div>

            {/* Current School Status Card */}
            <Card className="max-w-lg mx-auto bg-white/80 backdrop-blur-sm border-0 shadow-2xl">
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mb-4">
                  <GraduationCap className="h-8 w-8 text-white" />
                </div>
                {/*<CardTitle className="text-xl text-gray-900">
                  École Actuelle
                </CardTitle>*/}
                <CardDescription className="text-gray-600">
                  {t('invitations.connectedTeacherSchool')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* School Information */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <School className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-green-900">
                        {user.school_name || "École Inconnue"}
                      </h3>
                      <p className="text-sm text-green-700">
                        Statut : {t('invitations.connectedStatusTeacherSchool')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Features Available */}
                {/*<div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <Calendar className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                    <p className="text-xs text-blue-700 font-medium">{t('invitations.syncAgenda')}</p>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <Users className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                    <p className="text-xs text-purple-700 font-medium">{t('invitations.studentAccess')}</p>
                  </div>
                </div>*/}

                {/* Warning Section */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                    <h4 className="font-semibold text-yellow-800">Information</h4>
                  </div>
                  <p className="text-sm text-yellow-700">
                    {t('invitations.leaveInformation')}.
                  </p>
                </div>

                {/* Leave School Button */}
                <Button 
                  onClick={() => setShowLeaveConfirmModal(true)}
                  variant="destructive"
                  className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white py-3 text-base font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
                >
                  <LogOut className="h-5 w-5 mr-2" />
                  {t('invitations.leaveSchool')}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Leave Confirmation Modal */}
        <Dialog open={showLeaveConfirmModal} onOpenChange={setShowLeaveConfirmModal}>
          <DialogContent className="sm:max-w-md bg-gradient-to-br from-red-50 to-orange-50 border-0 shadow-2xl">
            <DialogHeader className="text-center space-y-4">
              <div className="mx-auto w-20 h-20 bg-gradient-to-r from-red-400 to-red-500 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="h-12 w-12 text-white" />
              </div>
              
              <DialogTitle className="text-2xl font-bold text-gray-900">
                 {t('invitations.isLeaveSchool')}
              </DialogTitle>
              
              <DialogDescription className="text-lg text-gray-700 space-y-4">
                <p>
                   {t('invitations.asLeaveSchoolDesc')} <strong>{user.school_name}</strong> ?
                </p>
                
                <div className="bg-white/60 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                    <UserX className="h-4 w-4 text-red-500" />
                    {t('invitations.consequences')} :
                  </h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• {t('invitations.consequence1')}</li>
                    <li>• {t('invitations.consequence2')}</li>
                    <li>• {t('invitations.consequence3')}</li>
                  </ul>
                </div>
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button 
                onClick={() => setShowLeaveConfirmModal(false)}
                variant="outline"
                className="flex-1 border-gray-300 hover:bg-gray-50"
              >
                {t('common.cancel')}
              </Button>
              
              <Button 
                onClick={handleLeaveSchool}
                disabled={isSubmitting}
                variant="destructive"
                className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {t('invitations.deconnectSchool')}...
                  </>
                ) : (
                  <>
                    <LogOut className="h-4 w-4 mr-2" />
                    {t('invitations.leaveSchool')}
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Coach view - join school with sports theme (default)
  return (
    <>
      <div className="min-h-[600px] bg-gradient-to-br from-blue-50 via-white to-purple-50 rounded-xl p-8 relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-200/30 to-purple-200/30 rounded-full blur-3xl -mr-32 -mt-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-blue-200/30 to-orange-200/30 rounded-full blur-3xl -ml-24 -mb-24"></div>
        
        <div className="relative z-10">
          {/* Header Section */}
          <div className="text-center mb-8">
            {/*<div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-orange-500 to-red-500 rounded-full mb-6 shadow-lg">
              <Link2 className="h-10 w-10 text-white" />
            </div>*/}
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {t('invitations.joinSchool')}
            </h1>
            <p className="text-lg text-gray-600 max-w-md mx-auto">
              {/*t('invitations.toJoinDesc')*/}
              {t('invitations.enterInvitationCode')}
            </p>
          </div>

           {/* Invitation Form */}
          <Card className="max-w-md mx-auto bg-white/80 backdrop-blur-sm border-0 shadow-2xl mb-12">
            <CardHeader className="text-center">
              <CardTitle className="flex items-center justify-center gap-2 text-xl text-gray-900">
                <Link2 className="h-5 w-5 text-orange-500" />
                {t('invitations.invitationCode')}
              </CardTitle>
              {/*<CardDescription className="text-gray-600">
                {t('invitations.enterInvitationCode')}
              </CardDescription>*/}
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                {/*<Label htmlFor="invitation-code" className="text-gray-900 font-medium">
                  {t('invitations.invitationCode')}
                </Label>*/}
                <div className="relative">
                  <Input
                    id="invitation-code"
                    placeholder={t('invitations.placeholder')}
                    value={invitationCode}
                    onChange={(e) => setInvitationCode(e.target.value)}
                    className="text-center text-lg font-mono border-gray-300 focus:ring-orange-500 focus:border-orange-500 py-3"
                  />
                  <Zap className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-orange-400" />
                </div>
              </div>
              
              <Button 
                onClick={handleJoinSchool}
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white py-3 text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      {t('invitations.connexion')}...
                  </>
                ) : (
                  <>
                    <Link2 className="h-5 w-5 mr-2" />
                    {t('invitations.joinSchoolBtn')}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="text-center p-6 bg-white/50 backdrop-blur-sm rounded-xl border border-white/20 shadow-lg">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mb-4">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('invitations.syncAgenda')}</h3>
              <p className="text-sm text-gray-600">{t('invitations.syncAgendaDesc')}</p>
            </div>
            
            <div className="text-center p-6 bg-white/50 backdrop-blur-sm rounded-xl border border-white/20 shadow-lg">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg mb-4">
                <Target className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('invitations.studentAccess')}</h3>
              <p className="text-sm text-gray-600">{t('invitations.studentAccessDesc')}</p>
            </div>
            
            <div className="text-center p-6 bg-white/50 backdrop-blur-sm rounded-xl border border-white/20 shadow-lg">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-lg mb-4">
                <Award className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('invitations.condifdentiality')}</h3>
              <p className="text-sm text-gray-600">{t('invitations.condifdentialityDesc')}</p>
            </div>
          </div>

         
        </div>
      </div>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-md bg-gradient-to-br from-green-50 to-blue-50 border-0 shadow-2xl">
          <DialogHeader className="text-center space-y-4">
            {/* Animated Success Icon */}
            <div className="mx-auto w-20 h-20 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center mb-4 animate-pulse">
              <CheckCircle className="h-12 w-12 text-white" />
            </div>
            
            <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center justify-center gap-2">
              <Trophy className="h-6 w-6 text-yellow-500" />
              {t('invitations.congratulations')} !
            </DialogTitle>
            
            <DialogDescription className="text-lg text-gray-700 space-y-3">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Star className="h-5 w-5 text-yellow-400 fill-current" />
                <span className="font-semibold">
                  {t('invitations.welcomeToSchool')} {schoolJoined?.name || t('invitations.yourNewSchool')} !
                </span>
                <Star className="h-5 w-5 text-yellow-400 fill-current" />
              </div>
              
              <p>
                {t('invitations.successfullJoined')} !
              </p>
              
              <div className="bg-white/60 rounded-lg p-4 mt-4">
                <div className="flex items-center justify-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4 text-blue-500" />
                    <span>Agenda</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4 text-green-500" />
                    <span>Étudiants</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Award className="h-4 w-4 text-purple-500" />
                    <span>Confidentialité</span>
                  </div>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button 
              onClick={() => {
                setShowSuccessModal(false);
                window.location.href = '/agenda';
              }}
              className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-6 py-3 font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
            >
              <Calendar className="h-5 w-5 mr-2" />
              Voir l'Agenda
            </Button>
            
            <Button 
              onClick={() => {
                setShowSuccessModal(false);
                window.location.href = '/settings';
              }}
              className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-6 py-3 font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
            >
              <Settings className="h-5 w-5 mr-2" />
              Gérer les Doublons
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InvitationJoinStudentManager;